import { Grid, Icons, Text } from "@/theme/index";
import { scrollToElement } from "@/utils/index";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
} from "@mui/material";
import { useState } from "react";
import type React from "react";
import { ReportNavigationMenu } from "./ReportNavigationMenu";
import type { ReportMenuItem } from "./components/ReportNavigationMenuItem";
import { ReportNavigationSubheader } from "./components/ReportNavigationSubheader";

interface ReportNavigationMobileMenuProps {
  headerTitle: string;
  reportType: string;
  menuItems: ReportMenuItem[];
  isInMobileComponent?: boolean;
}

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: "none",
  display: "block",
  border: `1px solid ${theme.palette.grey[300]}`,
  position: "fixed",
  bottom: 0,
  width: "100%",
  zIndex: 15,

  [theme.breakpoints.up("lg")]: {
    display: "none",
  },
  "&.Mui-expanded": {
    height: "calc(100% - 50px)",
  },
}));

export const ReportNavigationMobileMenu = ({
  headerTitle,
  reportType,
  menuItems,
}: ReportNavigationMobileMenuProps) => {
  const [sectionName, setSectionName] = useState<string | undefined>(undefined);
  const [currentPosition, setCurrentPosition] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [transformStyle] = useState({
    transform: "translateY(0)",
    transition: "transform 0.3s ease-in-out",
  });

  const handleClose = (event: { stopPropagation: () => void }) => {
    event.stopPropagation();
    setExpanded(false);
  };

  const AccordionSummaryStyles = {
    margin: 0,
    ".MuiAccordionSummary-content.Mui-expanded": {
      marginBottom: 0,
    },
  };

  const flattenedItems: string[] = menuItems.reduce((acc, item) => {
    const hrefs = [
      item.href,
      ...(item.childItems?.map((child) => child.href) || []),
    ].filter(Boolean) as string[];
    return acc.concat(hrefs);
  }, [] as string[]);

  const handleNextSection = (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentIndex = flattenedItems.findIndex((section) => {
      return section === currentPosition;
    });

    if (currentIndex !== -1 && currentIndex < flattenedItems.length - 1) {
      scrollToElement(flattenedItems[currentIndex + 1]?.slice(1));
    }
    return null; // No next section
  };

  const handlePreviousSection = (e: React.MouseEvent) => {
    e.stopPropagation();
    const currentIndex = flattenedItems.findIndex((section) => {
      return section === currentPosition;
    });

    if (currentIndex > 0) {
      scrollToElement(flattenedItems[currentIndex - 1]?.slice(1));
    }
    return null; // No previous section
  };

  const { icons, href = "" } = menuItems[0];
  return (
    <StyledAccordion expanded={expanded} sx={transformStyle}>
      {!expanded && (
        <AccordionSummary
          aria-controls="panel1a-content"
          id="panel1a-header"
          onClick={() => setExpanded(true)}
          sx={AccordionSummaryStyles}
        >
          <Grid container>
            <>
              <Grid item container alignItems="center" xs={8}>
                {sectionName !== "Overview" ? (
                  <Text color="primary" variant="h6" margin={0}>
                    {sectionName}
                  </Text>
                ) : (
                  <ReportNavigationSubheader
                    headerIcon={icons?.[0]}
                    headerTitle={headerTitle}
                    overviewSectionHref={href}
                    reportType={reportType}
                    isInSummary
                    collapseSummaryFn={() => setExpanded(true)}
                  />
                )}
              </Grid>
              <Grid item sx={{ marginLeft: "auto" }}>
                <Icons.ExpandLess
                  color="primary"
                  fontSize="large"
                  onClick={handlePreviousSection}
                />
                <Icons.ExpandMore
                  color="primary"
                  fontSize="large"
                  onClick={handleNextSection}
                />
              </Grid>
            </>
          </Grid>
        </AccordionSummary>
      )}
      <AccordionDetails
        sx={{
          maxHeight: "85vh",
          overflow: "scroll",
          paddingTop: 0,
          marginTop: "16px",
        }}
      >
        <ReportNavigationMenu
          headerTitle={headerTitle}
          reportType={reportType}
          menuItems={menuItems}
          isInMobileComponent
          handleCloseMenu={handleClose}
          setActiveSection={setSectionName}
          setSectionPosition={setCurrentPosition}
        />
      </AccordionDetails>
    </StyledAccordion>
  );
};
